import {
    Cartesian2, Cartesian3,
    Cartographic,
    Color,
    defined,
    GeometryInstance,
    HeightReference,
    LabelStyle,
    Material,
    PolylineGeometry,
    PolylineMaterialAppearance,
    Primitive,
    ScreenSpaceEventHandler,
    ScreenSpaceEventType,
    VerticalOrigin,
    Viewer
} from "cesium";
import { stateGlb } from "../../assets/encodeBase64/stateGlb";
import { base64ToFile, createUUID, showStatusStack } from "../../utils/utils";
import { CesiumSnap } from "./cesiumSnap";
import { getMessage } from "../../language";

export interface ActiveEntity {
    lineHypotenuse?: any;
    lineVertical?: any;
    lineHorizontal?: any;
    labelHypotenuse?: any;
    labelVertical?: any;
    labelHorizontal?: any;
    startDot?: any;
    endDot?: any;
    thirdDot?: any;
    labelEndPoint?: any
}

export class MeasureCesium {
    viewer: any;
    private handler: any
    containerId: string;
    private _fileTest = URL.createObjectURL(base64ToFile(stateGlb(), "stage.glb"))
    cesiumSnap: CesiumSnap;
    private _statusStackItem?: { remove: () => void };

    constructor(containerId: string, viewer: any) {
        this.containerId = containerId;
        this.viewer = viewer;
        this.cesiumSnap = new CesiumSnap(viewer);
    }



    activeShapePoints: any = [];
    activeShape: any;
    floatingPoint: any;
    currentPointClick: any;
    activeEntities: { [entityIdBase: string]: ActiveEntity } = {};
    currentLabelDim: any;
    isClickListenerMouseEnabled: any = false;
    pointXMark: any
    private _planeEntity: any

    dimensionCesium = () => {
        this.removeAllEvents();

        if (!this._statusStackItem) {
            this._statusStackItem = showStatusStack(this.containerId, getMessage("measure-state"));
        }

        if (!this.isClickListenerMouseEnabled) {
            document.addEventListener('click', this.handleDocumentEvents);
            document.addEventListener('keydown', this.handleDocumentEvents);
            this.isClickListenerMouseEnabled = true;
        }

        this.cesiumSnap.enable();

        this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);

        this.handler.setInputAction((click: ScreenSpaceEventHandler.PositionedEvent) => {
            let cartesian = (this.cesiumSnap.isSnapped && this.cesiumSnap.lastSnappedPosition)
                ? this.cesiumSnap.lastSnappedPosition
                : this.cesiumSnap.getSnappedPosition(click.position);

            if (cartesian) {
                let pickedObject = this.viewer.scene.drillPick(click.position);

                if (pickedObject.length == 0 && !this.cesiumSnap.isSnapped) {
                    let ray = this.viewer.camera.getPickRay(click.position);
                    cartesian = this.viewer.scene.globe.pick(ray!, this.viewer.scene) as Cartesian3;
                }

                if (!this._isDrawingDim) {
                    this.activeShapePoints.push(cartesian);
                    this._isDrawingDim = true;
                } else {
                    this.activeShapePoints.push(cartesian);
                    if (this._idTempDim && this._objTempDim) {
                        this.deleteEntityByPrefix(this._idTempDim, this._objTempDim);
                    }

                    const newidDim = createUUID()

                    if (!this.isMultiDim) {
                        if (this._planeEntity) {
                            this.viewer.entities.remove(this._planeEntity)
                            this._planeEntity = undefined
                        }
                        this._isDrawingDim = false;
                        this.activeEntities[newidDim] = this.createDimensionObjects(
                            this.activeShapePoints[0],
                            this.activeShapePoints[2],
                            newidDim
                        );
                        this.activeShapePoints = []
                    } else {
                        if (!this._idMultiDim) {
                            this._idMultiDim = createUUID()
                        }
                        this._countMultiDim++
                        this.activeEntities[this._idMultiDim + "-multi-dim-" + this._countMultiDim] = this.createMultiDimensionObjects(
                            this.activeShapePoints[0],
                            this.activeShapePoints[2],
                            this._idMultiDim + "-multi-dim-" + this._countMultiDim
                        );
                        this._totalMutilDim += Cartesian3.distance(this.activeShapePoints[0], this.activeShapePoints[2])
                        this.activeEntities[this._idMultiDim + "-multi-dim-" + this._countMultiDim].labelEndPoint = this._labelEndPoint

                        this.activeShapePoints.splice(0, 2)
                    }
                }
            }
            this.viewer.scene.requestRender();

        }, ScreenSpaceEventType.LEFT_CLICK)

        this.handler.setInputAction((move: ScreenSpaceEventHandler.MotionEvent) => {
            this.cesiumSnap.updateSnapThrottled(move.endPosition, (cartesian) => {
                if (this.activeShapePoints.length > 0 && this._isDrawingDim) {
                    if (this._idTempDim && this._objTempDim) {
                        this.deleteEntityByPrefix(this._idTempDim, this._objTempDim);
                        if (this._planeEntity) {
                            this._planeEntity.show = false;
                        }
                    }
                    this.viewer.scene.requestRender();

                    if (cartesian) {
                        if (this.activeShapePoints.length > 1) {
                            this.activeShapePoints.pop();
                        }

                        if (this._planeEntity) {
                            this._planeEntity.show = true;
                            this._planeEntity.position = cartesian;
                        }
                        this._idTempDim = createUUID();
                        this.activeShapePoints.push(cartesian);
                        if (!this.isMultiDim) {
                            this._objTempDim[this._idTempDim] = this.createDimensionObjects(
                                this.activeShapePoints[0],
                                this.activeShapePoints[1],
                                this._idTempDim
                            );
                        } else {
                            if (this._labelEndPoint) {
                                this._labelEndPoint.position = this.activeShapePoints[1];
                            }

                            this._objTempDim[this._idTempDim] = this.createMultiDimensionObjects(
                                this.activeShapePoints[0],
                                this.activeShapePoints[1],
                                this._idTempDim
                            );
                        }
                        this.viewer.scene.requestRender();
                    }
                } else {
                    this.viewer.scene.requestRender();
                }
            });
        }, ScreenSpaceEventType.MOUSE_MOVE);

        this.handler.setInputAction(() => {
            this.terminateDim();
            this.viewer.scene.requestRender();
        }, ScreenSpaceEventType.RIGHT_CLICK);
    }

    private _isDrawingDim = false
    isMultiDim = false
    private _objTempDim: any = {}
    private _idTempDim = ""
    private _idMultiDim = ""
    private _countMultiDim = 0

    private terminateDim = () => {
        this.cesiumSnap.hideSnapIndicator();
        if (this._idTempDim && this._objTempDim) {
            this.deleteEntityByPrefix(this._idTempDim, this._objTempDim);
        }
        if (this._labelEndPoint) {
            this._labelEndPoint.position = this.activeShapePoints[0]
            this._labelEndPoint.label.text = `~ ${this._totalMutilDim.toFixed(2)} m`

            if (this._totalMutilDim == 0) {
                this.viewer.entities.remove(this._labelEndPoint)
            }
            this._labelEndPoint = undefined
        }
        if (this._planeEntity) {
            this.viewer.entities.remove(this._planeEntity)
            this._planeEntity = undefined
        }
        this._totalMutilDim = 0
        this._idMultiDim = ""
        this._countMultiDim = 0
        this._isDrawingDim = false
        this._objTempDim = {}
        this._idTempDim = ""

        this.activeShapePoints = []
    }

    private _totalMutilDim = 0
    private _labelEndPoint: any

    private createMultiDimensionObjects = (startPoint: any, endPoint: any, entityIdBase: any, disableDepthEndPoint = false) => {
        var startDotEntity = this.createPointDim(startPoint, `${entityIdBase}-dim-startPoint`, false);
        const polylineHypotenuse = this.createPolylinePrimitive(startPoint, endPoint, `${entityIdBase}-line-hypotenuse`, Color.CRIMSON.withAlpha(0.8));
        const distanceHypotenuse = Cartesian3.distance(startPoint, endPoint);
        const newDistance = this._totalMutilDim + distanceHypotenuse

        let endDotEntity
        if (!disableDepthEndPoint) {
            endDotEntity = this.createPointDim(endPoint, `${entityIdBase}-dim-endPoint`, false);
        }

        if (!this._labelEndPoint) {
            this._labelEndPoint = this.createLabelForPoint(endPoint, `${entityIdBase}-label-endPoint`, false, false, `~ ${newDistance.toFixed(2)} m`, Color.CORAL.withAlpha(0.8));
        } else {
            this._labelEndPoint.label.text = `~ ${newDistance.toFixed(2)} m`
        }

        const labelHypotenuse = this.createLabelForDimension(startPoint, endPoint, distanceHypotenuse, `${entityIdBase}-label-hypotenuse`, Color.CRIMSON.withAlpha(0.75));
        return {
            lineHypotenuse: polylineHypotenuse,
            lineVertical: undefined,
            lineHorizontal: undefined,
            labelHypotenuse: labelHypotenuse,
            labelVertical: undefined,
            labelHorizontal: undefined,
            startDot: startDotEntity,
            endDot: endDotEntity,
            thirdDot: undefined,
        };
    }

    private createDimensionObjects = (startPoint: any, endPoint: any, entityIdBase: any, disableDepthEndPoint = false) => {
        var startDotEntity = this.createPointDim(startPoint, `${entityIdBase}-dim-startPoint`, false);

        let endDotEntity
        if (!disableDepthEndPoint) {
            endDotEntity = this.createPointDim(endPoint, `${entityIdBase}-dim-endPoint`, false);
        }

        const startCartographic = Cartographic.fromCartesian(startPoint);
        const endCartographic = Cartographic.fromCartesian(endPoint);

        const hStart = startCartographic.height;
        const hEnd = endCartographic.height;

        let thirdPoint: Cartesian3;
        let polylineVertical: any;
        let polylineHorizontal: any;
        let distanceVertical: number;
        let distanceHorizontal: number;
        let labelVertical: any;
        let labelHorizontal: any;

        if (hStart >= hEnd) {
            thirdPoint = Cartesian3.fromRadians(
                endCartographic.longitude,
                endCartographic.latitude,
                hStart
            );

            polylineVertical = this.createPolylinePrimitive(endPoint, thirdPoint, `${entityIdBase}-line-vertical`, Color.LIMEGREEN.withAlpha(0.8));
            polylineHorizontal = this.createPolylinePrimitive(startPoint, thirdPoint, `${entityIdBase}-line-horizontal`, Color.DODGERBLUE.withAlpha(0.8));

            distanceVertical = Cartesian3.distance(endPoint, thirdPoint);
            distanceHorizontal = Cartesian3.distance(startPoint, thirdPoint);

            labelVertical = this.createLabelForDimension(endPoint, thirdPoint, distanceVertical, `${entityIdBase}-label-vertical`, Color.LIMEGREEN.withAlpha(0.8));
            labelHorizontal = this.createLabelForDimension(startPoint, thirdPoint, distanceHorizontal, `${entityIdBase}-label-horizontal`, Color.DODGERBLUE.withAlpha(0.75));
        } else {
            thirdPoint = Cartesian3.fromRadians(
                startCartographic.longitude,
                startCartographic.latitude,
                hEnd
            );

            polylineVertical = this.createPolylinePrimitive(startPoint, thirdPoint, `${entityIdBase}-line-vertical`, Color.LIMEGREEN.withAlpha(0.8));
            polylineHorizontal = this.createPolylinePrimitive(thirdPoint, endPoint, `${entityIdBase}-line-horizontal`, Color.DODGERBLUE.withAlpha(0.8));

            distanceVertical = Cartesian3.distance(startPoint, thirdPoint);
            distanceHorizontal = Cartesian3.distance(thirdPoint, endPoint);

            labelVertical = this.createLabelForDimension(startPoint, thirdPoint, distanceVertical, `${entityIdBase}-label-vertical`, Color.LIMEGREEN.withAlpha(0.8));
            labelHorizontal = this.createLabelForDimension(thirdPoint, endPoint, distanceHorizontal, `${entityIdBase}-label-horizontal`, Color.DODGERBLUE.withAlpha(0.75));
        }

        var thirdDotEntity = this.createPointDim(thirdPoint, `${entityIdBase}-dim-thirdPoint`, false);
        const polylineHypotenuse = this.createPolylinePrimitive(startPoint, endPoint, `${entityIdBase}-line-hypotenuse`, Color.CRIMSON.withAlpha(0.8));
        const distanceHypotenuse = Cartesian3.distance(startPoint, endPoint);
        const labelHypotenuse = this.createLabelForDimension(startPoint, endPoint, distanceHypotenuse, `${entityIdBase}-label-hypotenuse`, Color.CRIMSON.withAlpha(0.75));

        return {
            lineHypotenuse: polylineHypotenuse,
            lineVertical: polylineVertical,
            lineHorizontal: polylineHorizontal,
            labelHypotenuse: labelHypotenuse,
            labelVertical: labelVertical,
            labelHorizontal: labelHorizontal,
            startDot: startDotEntity,
            endDot: endDotEntity,
            thirdDot: thirdDotEntity,
            labelEndPoint: undefined
        };
    }

    private createPointDim = (worldPosition: any, inputId: any, clamGound: boolean, disableDepth = true) => {
        const id = (inputId === null || inputId === undefined) ? createUUID() : inputId;
        const point = this.viewer.entities.add({
            id: id,
            position: worldPosition,
            point: {
                color: Color.WHITE,
                pixelSize: 6,
                outlineColor: Color.RED,
                outlineWidth: 2,
                heightReference: clamGound ? HeightReference.CLAMP_TO_GROUND : HeightReference.NONE,
                disableDepthTestDistance: disableDepth ? Number.POSITIVE_INFINITY : 0,
            },
        });
        return point;
    }

    private createLabelForPoint = (worldPosition: any, inputId: string, clamGound: boolean, disableDepth = true, labelText: string, inputColor: Color) => {
        const id = (inputId === null || inputId === undefined) ? createUUID() : inputId;

        const point = (this.viewer as Viewer).entities.add({
            id: id,
            position: worldPosition,
            label: {
                text: labelText,
                font: '10pt sans-serif',
                fillColor: Color.WHITESMOKE,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
                style: LabelStyle.FILL_AND_OUTLINE,
                outlineWidth: 4,
                outlineColor: Color.GREY,
                verticalOrigin: VerticalOrigin.BOTTOM,
                pixelOffset: new Cartesian2(0, -9),
                backgroundColor: inputColor || Color.BLACK.withAlpha(0.8),
                showBackground: true,
            },
        });
        return point;
    }

    private createPolylinePrimitive = (startPoint: any, endPoint: any, entityIdBase: any, color: any) => {
        let polyLineFromPri
        const polylineGeometry = new PolylineGeometry({
            positions: [startPoint, endPoint],
            width: 2.0,
            vertexFormat: PolylineMaterialAppearance.VERTEX_FORMAT,
        });

        const polylineInstance = new GeometryInstance({
            geometry: polylineGeometry,
            id: `${entityIdBase}`,
        });

        const polylinePrimitive = new Primitive({
            geometryInstances: polylineInstance,
            appearance: new PolylineMaterialAppearance({
                material: Material.fromType('Color', {
                    color: color
                }),
                translucent: false
            }),
            depthFailAppearance: new PolylineMaterialAppearance({
                material: Material.fromType('Color', {
                    color: color
                }),
                translucent: false
            }),
            asynchronous: false
        });

        polyLineFromPri = this.viewer.scene.primitives.add(polylinePrimitive);
        return polyLineFromPri;
    }

    private createLabelForDimension = (startPoint: any, endPoint: any, distance: any, inputId: any, inputColor?: Color) => {
        const midPoint = Cartesian3.midpoint(startPoint, endPoint, new Cartesian3());
        const id = (inputId === null || inputId === undefined) ? createUUID() : inputId;
        let label = (this.viewer as Viewer).entities.add({
            id: id,
            position: midPoint,
            label: {
                text: "~ " + distance.toFixed(2) + ' m',
                font: '10pt sans-serif',
                fillColor: Color.WHITESMOKE,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
                style: LabelStyle.FILL_AND_OUTLINE,
                outlineWidth: 3,
                outlineColor: Color.GREY,
                verticalOrigin: VerticalOrigin.BOTTOM,
                pixelOffset: new Cartesian2(0, -9),
                backgroundColor: inputColor || Color.BLACK.withAlpha(0.8),
                showBackground: true,
            },
        });

        return label;
    }

    handleDocumentEvents = (event: any) => {
        if (event.type === 'click' && !this.viewer.scene.canvas.contains(event.target)) {
            this.terminateDim();
            this.viewer.scene.requestRender();
        }

        if (event.type === 'keydown' && (event.key === 'Escape' || event.key === 'Esc')) {
            this.terminateDim();
            this.viewer.scene.requestRender();
        }
    }

    removeDimCesium = () => {
        this.removeAllEvents();
        this.terminateDim();
        this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);
        this.handler.setInputAction((event: any) => {
            const pickedEntity = this.viewer.scene.pick(event.position);

            if (defined(pickedEntity)) {
                var entityId
                if (typeof pickedEntity.id === "string") {
                    entityId = pickedEntity.id;
                } else if (pickedEntity.id?.id) {
                    entityId = pickedEntity.id.id;
                }
                var entityIdParts = entityId?.split('-').slice(0, -2).join('-');
                if (entityIdParts?.includes("-multi-dim-")) {
                    const newIdEntity = entityIdParts.split('-').slice(0, -1).join('-');
                    Object.keys(this.activeEntities).forEach(key => {
                        if (key.includes(newIdEntity))
                            this.deleteEntityByPrefix(key, this.activeEntities)
                    });
                } else {
                    this.deleteEntityByPrefix(entityIdParts, this.activeEntities);
                }
            }
            this.viewer.scene.requestRender();

        }, ScreenSpaceEventType.LEFT_CLICK)
    }

    private removeAllEvents = () => {
        if (this.handler) {
            if (typeof this.handler.destroy === "function" && !this.handler.isDestroyed()) {
                this.handler.destroy();
            }
            this.handler = undefined;
        }
        this.cesiumSnap.disable();
    }

    private deleteEntityByPrefix = (entityIdBase: any, objEntities: any) => {
        if (objEntities[entityIdBase]) {
            this.viewer.scene.primitives.remove(objEntities[entityIdBase].lineHypotenuse);
            this.viewer.scene.primitives.remove(objEntities[entityIdBase].lineVertical);
            this.viewer.scene.primitives.remove(objEntities[entityIdBase].lineHorizontal);

            this.viewer.entities.remove(objEntities[entityIdBase].labelHypotenuse);
            this.viewer.entities.remove(objEntities[entityIdBase].labelVertical);
            this.viewer.entities.remove(objEntities[entityIdBase].labelHorizontal);
            this.viewer.entities.remove(objEntities[entityIdBase].startDot);
            this.viewer.entities.remove(objEntities[entityIdBase].endDot);
            this.viewer.entities.remove(objEntities[entityIdBase].thirdDot);
            this.viewer.entities.remove(objEntities[entityIdBase].labelEndPoint);

            delete objEntities[entityIdBase];
        }
    }

    removeAllDimensionCesium = () => {
        Object.keys(this.activeEntities).forEach(key => {
            this.deleteEntityByPrefix(key, this.activeEntities)
        });

        this.removeAllEvents();
        this.viewer.scene.requestRender();
    }

    showHideAllDimensionCesium = (showEntity: boolean) => {
        Object.keys(this.activeEntities).forEach(key => {
            this.showHideDimension(key, showEntity)
        });

        this.viewer.scene.requestRender();
    }

    private showHideDimension = (entityIdBase: any, showEntity: boolean) => {
        if (this.activeEntities[entityIdBase]) {
            const entity = this.activeEntities[entityIdBase];

            for (const property in entity) {
                if (entity[property as keyof ActiveEntity] && 'show' in entity[property as keyof ActiveEntity]) {
                    (entity[property as keyof ActiveEntity] as any).show = showEntity;
                }
            }

            this.removeAllEvents();
        }
    }

    refreshAllAction = () => {
        this.terminateDim();
        this.isMultiDim = false
        document.removeEventListener("click", this.handleDocumentEvents)
        document.removeEventListener("keydown", this.handleDocumentEvents)
        this.isClickListenerMouseEnabled = false;
        if (this._statusStackItem) {
            this._statusStackItem.remove();
            this._statusStackItem = undefined;
        }
        this.removeAllEvents();
        this.viewer.scene.requestRender();
    }

    destroy = () => {
        this.refreshAllAction();
        this.removeAllDimensionCesium();
        if (this._fileTest) {
            URL.revokeObjectURL(this._fileTest);
        }
    }
}
